import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Crosshair } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card';

interface CrossShotDistributionCardProps {
  shotsBreakdown: {
    summary?: {
      total_shots?: number;
      offensive_total?: number;
      defensive_total?: number;
    };
    offensive_shots?: Record<string, number>;
    defensive_shots?: Record<string, number>;
  } | null;
  delay?: number;
}

/** Advanced-mode only: total shot mix across all sessions. Mirrors the
 * offensive/defensive split layout from ShotAnalysis without per-player UI. */
const CrossShotDistributionCard: React.FC<CrossShotDistributionCardProps> = ({
  shotsBreakdown,
  delay = 0,
}) => {
  const { t } = useTranslation(['report']);

  const summary = shotsBreakdown?.summary ?? {};
  const offensive = shotsBreakdown?.offensive_shots ?? {};
  const defensive = shotsBreakdown?.defensive_shots ?? {};

  const total = summary.total_shots ?? 0;
  const offTotal = summary.offensive_total ?? 0;
  const defTotal = summary.defensive_total ?? 0;

  const offRatio = total > 0 ? offTotal / total : 0;
  const defRatio = total > 0 ? defTotal / total : 0;

  const offEntries = sortEntries(offensive).slice(0, 4);
  const defEntries = sortEntries(defensive).slice(0, 4);

  return (
    <Card style={{ animation: `fade-in 0.5s ease-out ${delay}ms both` }}>
      <CardHeader className="flex flex-row items-center gap-3 space-y-0">
        <div className="rounded-lg bg-slate-600 p-2">
          <Crosshair className="h-5 w-5 text-white" />
        </div>
        <div className="space-y-0.5">
          <CardTitle className="text-lg">
            {t('report:playerReport.shotDistribution')}
          </CardTitle>
          <CardDescription className="text-sm">
            {t('report:playerReport.shotDistributionSubtitle', {
              count: total,
            })}
          </CardDescription>
        </div>
      </CardHeader>

      <CardContent>
        <div className="flex h-3 overflow-hidden rounded-full bg-slate-200">
          <div
            className="h-full bg-slate-900"
            style={{ width: `${offRatio * 100}%` }}
          />
          <div
            className="h-full bg-slate-500"
            style={{ width: `${defRatio * 100}%` }}
          />
        </div>
        <div className="mt-2 flex justify-between text-xs text-slate-500">
          <span>
            {t('report:shot.offensive')}: {Math.round(offRatio * 100)}%
          </span>
          <span>
            {t('report:shot.defensive')}: {Math.round(defRatio * 100)}%
          </span>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4">
          <Breakdown
            title={t('report:shot.offensive')}
            entries={offEntries}
            accentClass="bg-slate-900"
          />
          <Breakdown
            title={t('report:shot.defensive')}
            entries={defEntries}
            accentClass="bg-slate-500"
          />
        </div>
      </CardContent>
    </Card>
  );
};

const Breakdown: React.FC<{
  title: string;
  entries: Array<[string, number]>;
  accentClass: string;
}> = ({ title, entries, accentClass }) => (
  <div>
    <h3 className="mb-2 text-sm font-semibold text-slate-700">{title}</h3>
    {entries.length === 0 ? (
      <p className="text-xs text-slate-400">—</p>
    ) : (
      <ul className="space-y-1">
        {entries.map(([type, count]) => (
          <li
            key={type}
            className="flex items-center justify-between text-xs text-slate-600"
          >
            <span className="flex items-center gap-2">
              <span className={`h-2 w-2 rounded-full ${accentClass}`} />
              {type}
            </span>
            <span className="font-semibold text-slate-700">
              {Math.round(count)}
            </span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

function sortEntries(record: Record<string, number>): Array<[string, number]> {
  return Object.entries(record)
    .filter(([, v]) => typeof v === 'number' && v > 0)
    .sort((a, b) => b[1] - a[1]);
}

export default CrossShotDistributionCard;
